"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
var rxjs_1 = require("rxjs");
var noOp = function () { };
/**
 * A representation of an Application state.`ApplicationStore` is the state instance that
 * turns each key in its constructor args into a method from updating that state property.
 * (e.g. `state.users` = ApplicationStore.users( ... ))
 */
var _ApplicationStore = /** @class */ (function () {
    function _ApplicationStore(state) {
        var _this = this;
        Object.defineProperty(this, "subject", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Hold all subscriptions for one-shot reset */
        Object.defineProperty(this, "allSubscriptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Subscribers/Listeners that are called when the state changes */
        Object.defineProperty(this, "subscribers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** @private Copy of original state args */
        Object.defineProperty(this, "ref", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** Instance state that gets updated. */
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        /* State requires at least one key */
        if (Object.keys(state).length < 1) {
            var msg = "'state' needs to be an object with at least one key";
            throw new Error(msg);
        }
        // Initialize application source-of-truth
        this.state = __assign({}, state);
        this.ref = Object.freeze(state);
        this.subject = new rxjs_1.Subject();
        this.subscribers = [];
        this.allSubscriptions = this.subject.subscribe({ next: function () { } });
        var _loop_1 = function (key) {
            var updater = function (value) {
                var _a;
                if (value === void 0) { value = null; }
                var updated = Object.assign({}, _this.state, (_a = {}, _a[key] = value, _a));
                return _this.updateState(updated, [key]);
            };
            // (this as any)[key] = updater;
            this_1[key] = updater;
        };
        var this_1 = this;
        // Turn every key in the `state` representation into a method on the instance.
        for (var key in state) {
            _loop_1(key);
        }
    }
    /** Get [a copy of] the current application state */
    Object.defineProperty(_ApplicationStore.prototype, "getState", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function () {
            return Object.assign({}, __assign({}, this.state));
        }
    });
    /**
     * Update multiple keys in state before notifying subscribers.
     * @param {Partial<ApplicationState>} changes Data source for state updates. This
     * is an object with one or more state keys that need to be updated.
     */
    Object.defineProperty(_ApplicationStore.prototype, "multiple", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (changes) {
            var _this = this;
            if (typeof changes !== "object") {
                throw new Error("State updates need to be a key-value object literal");
            }
            var changeKeys = Object.keys(changes);
            var updated = __assign({}, this.state);
            changeKeys.forEach(function (key) {
                var _a;
                if (!_this[key]) {
                    throw new Error("There is no \"".concat(key, "\" in this state instance."));
                }
                else {
                    updated = __assign(__assign({}, updated), (_a = {}, _a[key] = changes[key], _a));
                }
            });
            return this.updateState(updated, changeKeys);
        }
    });
    /** Reset the instance to its initialized state. Preserve subscribers. */
    Object.defineProperty(_ApplicationStore.prototype, "reset", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (clearSubscribers) {
            if (clearSubscribers === void 0) { clearSubscribers = false; }
            // reset state to initial values without notifying subscribers
            this.state = __assign({}, this.ref);
            if (clearSubscribers) {
                this.allSubscriptions.unsubscribe();
                this.subscribers = [];
            }
        }
    });
    /**
     * Subscribe to the state instance. Returns an `unsubscribe` function
     * @param listener Listener function
     * @returns Unsubscribe function
     */
    Object.defineProperty(_ApplicationStore.prototype, "subscribe", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (listener) {
            var invalidListener = validateListener(listener);
            if (invalidListener)
                throw new Error(invalidListener);
            // Return no-operation if already subscribed
            if (isSubscribed(listener, this.subscribers))
                return noOp;
            var subscription = this.subject.subscribe({
                next: function (vals) {
                    var _a = __read(vals, 2), updatedState = _a[0], updatedKeys = _a[1];
                    listener(updatedState, updatedKeys);
                },
            });
            return this.handleUnsubscription(subscription, listener);
        }
    });
    Object.defineProperty(_ApplicationStore.prototype, "handleUnsubscription", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (subscription, listener) {
            var _this = this;
            if (listener) {
                this.allSubscriptions.add(subscription);
                this.subscribers.push(listener);
            }
            return function () {
                subscription.unsubscribe();
                if (!listener)
                    return;
                _this.allSubscriptions.remove(subscription);
                _this.subscribers = _this.subscribers.filter(function (l) { return l !== listener; });
            };
        }
    });
    /**
     * Subscribe until a specified `key` is updated, then unsubscribe. Optionally takes
     * a value-checker in case you want to subscribe until a particular value is received.
     * It creates a new subscriber that triggers the listener when `key` is updated, and/or
     * when the value of `key` passes the `valueCheck` function (if one is supplied).
     * @param listener Listener function
     * @param key Key to check for updates
     * @param expectValue Optional function to assert the value of `key` when it updates.
     * @returns Unsubscribe function
     */
    Object.defineProperty(_ApplicationStore.prototype, "subscribeOnce", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (listener, key, expectValue) {
            var invalidListener = validateListener(listener);
            if (invalidListener)
                throw new Error(invalidListener);
            var k = (key || "").toString();
            var subscription = (0, rxjs_1.from)(this.subject)
                .pipe((0, rxjs_1.filter)(function (val) {
                var match = k === "" || val[1].includes(k);
                return match || (expectValue ? expectValue(val[0][k]) : true);
            }))
                .subscribe({
                next: function (vals) {
                    var _a = __read(vals, 2), state = _a[0], updated = _a[1];
                    // Exit if key was not updated, or does not have an expected value
                    var wasUpdated = !key || updated.includes(k);
                    var isExpected = !expectValue || expectValue(state[k]);
                    var notify = wasUpdated && isExpected;
                    // Trigger the listener
                    if (notify) {
                        unsubscribeListener();
                        return listener(state, [k]);
                    }
                },
            });
            var unsubscriber = this.handleUnsubscription(subscription);
            return unsubscribeListener;
            function unsubscribeListener() {
                unsubscriber();
            }
        }
    });
    /**
     * Subscribe to changes applied to a subset of state properties.
     * @param listener Listener function
     * @param keys List of state keys to "watch" for updates
     * @param valueCheck Optional function to assert the value of `key` when it updates.
     * @returns Unsubscribe function
     */
    Object.defineProperty(_ApplicationStore.prototype, "subscribeToKeys", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (listener, keys, valueCheck) {
            if (valueCheck === void 0) { valueCheck = function (k, v) { return true; }; }
            var invalidListener = validateListener(listener);
            if (invalidListener)
                throw new Error(invalidListener);
            // construct a custom subscriber
            var subscription = (0, rxjs_1.from)(this.subject)
                .pipe((0, rxjs_1.filter)(function (_a) {
                var _b = __read(_a, 2), s = _b[0], updatedKeys = _b[1];
                var hasRelevantKeys = updatedKeys.some(function (uk) { return keys.includes(uk); });
                return hasRelevantKeys;
            }), (0, rxjs_1.map)(function (_a) {
                var _b = __read(_a, 2), s = _b[0], updatedKeys = _b[1];
                // Copy updated values that appear in the `keys` list
                var rKeys = updatedKeys.filter(function (k) { return keys.includes(k); });
                var newState = rKeys.reduce(function (agg, key) {
                    var _a;
                    var matches = valueCheck(key, s[key]);
                    return matches ? __assign(__assign({}, agg), (_a = {}, _a[key] = s[key], _a)) : agg;
                }, {});
                return [newState, rKeys];
            }))
                .subscribe(function (vals) {
                var _a = __read(vals, 2), updated = _a[0], updatedKeys = _a[1];
                if (Object.keys(updated).length === 0)
                    return;
                listener(updated, updatedKeys);
            });
            // return an unsubscribe function
            return this.handleUnsubscription(subscription, listener);
        }
    });
    /**
     * @private Update the instance with changes, then notify subscribers with a copy
     */
    Object.defineProperty(_ApplicationStore.prototype, "updateState", {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function (updated, updatedKeys) {
            if (updatedKeys === void 0) { updatedKeys = []; }
            this.state = __assign({}, updated);
            this.subject.next([updated, updatedKeys]);
        }
    });
    return _ApplicationStore;
}());
/**
 * Assert a listener is not in fact subscribed
 * @param listener Listener function parameter
 * @returns Boolean assertion
 */
function isSubscribed(listener, subscribers) {
    var matchListener = subscribers.includes(listener);
    return matchListener;
}
/**
 * Return an error message when a param is not a function.
 * @param listener Listener function parameter
 * @returns {string|null} Error message or `null` if param is valid
 */
function validateListener(listener) {
    // This better be a function. Or Else.
    return typeof listener !== "function"
        ? "Invalid listener: '".concat(typeof listener, "' is not a function")
        : null;
}
/** Passed ref for retaining type definitions */
var ApplicationStore = _ApplicationStore;
function createState(initialState) {
    return new ApplicationStore(initialState);
}
exports.default = createState;
